import { useNavigate, useParams } from "react-router";
import {
  Header,
  Content,
  Wrapper,
  HeaderTitle,
  HeaderDescription,
  HeaderInner,
  TabContent,
  ScreenHeader,
} from "./AccountPage.styles";
import BreadCrumb from "../../components/Breadcrumb";
import { useAccount } from "../../hooks/useAccount";
import { Tab, TabList, TabPanel, Tabs } from "@gds-react/core";
import { AccountTabTypeEnum } from "../../types/AccountTabTypeEnum";
import { useEffect } from "react";
import { routes } from "../../utils/routeHelper";
import { WalletIcon } from "../../components/Icons/WalletSVG";
import Screen from "../../components/Screen";
import { DeviceTabTypeEnum } from "../../types/DeviceTabTypeEnum";
import { useStore } from "../../store/store";
import CoreWallet from "@gf-core/widgets/widgets/CoreWallet";
import { useTranslation } from "react-i18next";
import {
  ActiveAlertsWidget,
  AlertEventDto,
  AlertEventsWidget,
} from "@gf-alerting/widgets";
import {
  BellOutlined,
  ClockOutlined,
  InfoCircleOutlined,
} from "@gds-react/icons";
import AccountInformationTab from "./Tabs/AccountInformationTab";

export default function AccountPage() {
  const { t } = useTranslation();
  const setActiveMenuId = useStore.useSetActiveMenuId();
  const showAppStoreFeature = useStore.useShowAppStoreFeature();
  const { accountId, tabIndex } = useParams();
  const navigate = useNavigate();
  const { data: account, isLoading: isLoadingAccount } = useAccount(
    accountId,
    true,
  );

  const selectedTabValue =
    (tabIndex as AccountTabTypeEnum) ?? AccountTabTypeEnum.AccountInformation;

  useEffect(() => {
    setActiveMenuId(accountId);
  }, [accountId]);

  return (
    <Screen isAnimated>
      <ScreenHeader>
        <BreadCrumb
          list={account?.meta?.breadcrumbs ?? []}
          isLoading={isLoadingAccount}
        />
      </ScreenHeader>
      <Wrapper>
        <Header>
          <HeaderInner>
            <HeaderTitle> {t("accountPage.title")}</HeaderTitle>
            <HeaderDescription>
              {t("accountPage.description")}
            </HeaderDescription>
          </HeaderInner>
        </Header>

        <Content>
          <Tabs
            value={selectedTabValue}
            onValueChange={(value) => {
              navigate(
                routes.account.set(
                  accountId ?? "",
                  value as AccountTabTypeEnum,
                ),
              );
            }}
          >
            {[
              <TabList key="tab-list">
                {[
                  <Tab
                    key="account-information"
                    value={AccountTabTypeEnum.AccountInformation}
                    icon={
                      <InfoCircleOutlined style={{ width: 16, height: 16 }} />
                    }
                  >
                    {t("accountPage.tabs.accountInformation.title")}
                  </Tab>,
                  <Tab
                    key="active-alarms"
                    value={AccountTabTypeEnum.ActiveAlarms}
                    icon={<BellOutlined style={{ width: 16, height: 16 }} />}
                  >
                    {t("accountPage.tabs.activeAlarms.title")}
                  </Tab>,
                  <Tab
                    key="alarm-history"
                    value={AccountTabTypeEnum.AlarmHistory}
                    icon={<ClockOutlined style={{ width: 16, height: 16 }} />}
                  >
                    {t("accountPage.tabs.alarmHistory.title")}
                  </Tab>,
                  showAppStoreFeature && (
                    <Tab
                      key="wallet"
                      value={AccountTabTypeEnum.Wallet}
                      icon={<WalletIcon style={{ width: 16, height: 16 }} />}
                    >
                      Wallet
                    </Tab>
                  ),
                ].filter(Boolean)}
              </TabList>,
              <TabPanel
                key="account-information"
                value={AccountTabTypeEnum.AccountInformation}
              >
                <TabContent>
                  <AccountInformationTab accountId={accountId} />
                </TabContent>
              </TabPanel>,
              <TabPanel
                key="active-alarms"
                value={AccountTabTypeEnum.ActiveAlarms}
              >
                {accountId && (
                  <TabContent>
                    <ActiveAlertsWidget
                      type="page"
                      sourceId={accountId}
                      cellStyles={{ height: 48, minHeight: 48 }}
                      onRowClick={(event: AlertEventDto) => {
                        if (event.installationNodeId && event.source) {
                          navigate(
                            routes.device.set(
                              accountId,
                              event.installationNodeId,
                              event.source.id,
                              DeviceTabTypeEnum.ActiveAlerts,
                            ),
                          );
                        }
                      }}
                    />
                  </TabContent>
                )}
              </TabPanel>,
              <TabPanel
                key="alarm-history"
                value={AccountTabTypeEnum.AlarmHistory}
              >
                {accountId && (
                  <TabContent>
                    <AlertEventsWidget
                      type="page"
                      sourceId={accountId}
                      cellStyles={{ height: 48, minHeight: 48 }}
                      onRowClick={(event: AlertEventDto) => {
                        if (event.installationNodeId && event.source) {
                          navigate(
                            routes.device.set(
                              accountId,
                              event.installationNodeId,
                              event.source.id,
                              DeviceTabTypeEnum.AlertHistory,
                            ),
                          );
                        }
                      }}
                    />
                  </TabContent>
                )}
              </TabPanel>,
              showAppStoreFeature && (
                <TabPanel key="wallet" value={AccountTabTypeEnum.Wallet}>
                  <TabContent>
                    <CoreWallet
                      accountId={accountId}
                      isModal={false}
                      onUnassign={(
                        accountId: string,
                        installationId: string,
                        deviceId: string,
                      ) => {
                        navigate(
                          routes.device.set(
                            accountId,
                            installationId,
                            deviceId,
                            DeviceTabTypeEnum.Overview,
                            "core-device-widget",
                          ),
                        );
                      }}
                    />
                  </TabContent>
                </TabPanel>
              ),
            ].filter(Boolean)}
          </Tabs>
        </Content>
      </Wrapper>
    </Screen>
  );
}
